import { useState } from 'react';
import { ImportProcessor, type ImportProgress } from '@/lib/import-processor';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

export function ImportModal({ isOpen, onClose, onImportComplete }: ImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.json')) {
      setFile(droppedFile);
      setError(null);
    } else {
      setError('Please select a valid JSON file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    try {
      const processor = new ImportProcessor();
      await processor.processImport(file, (prog) => {
        setProgress(prog);

        // Handle completion
        if (prog.stage === 'completed') {
          onImportComplete();
          setTimeout(() => handleClose(), 1500);
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    }
  };

  const handleClose = () => {
    setFile(null);
    setProgress(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-bg-primary rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Import Data</h2>
          <button onClick={handleClose} className="text-text-secondary hover:text-white">
            ✕
          </button>
        </div>

        {/* Content based on state */}
        {!progress ? (
          <>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging ? 'border-accent bg-accent/10' : 'border-border'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="text-text-secondary mb-2">
                {file ? file.name : 'Drag and drop JSON file here'}
              </div>
              <label className="text-accent hover:underline cursor-pointer">
                or click to browse
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            </div>

            {error && <div className="text-red-500 mt-2 text-sm">{error}</div>}

            {file && (
              <button
                onClick={handleImport}
                className="w-full mt-4 px-4 py-2 bg-accent text-white rounded hover:bg-accent/90 transition"
              >
                Start Import
              </button>
            )}
          </>
        ) : (
          <div className="text-center">
            {progress.stage === 'completed' ? (
              <div>
                <div className="text-green-500 text-4xl mb-2">✓</div>
                <div className="text-lg font-bold mb-2">Import Complete!</div>
                <div className="text-sm text-text-secondary">
                  {progress.stats.tweets} tweets imported
                  {progress.stats.duplicates > 0 && `, ${progress.stats.duplicates} duplicates skipped`}
                </div>
              </div>
            ) : progress.stage === 'error' ? (
              <div>
                <div className="text-red-500 text-4xl mb-2">✕</div>
                <div className="text-lg font-bold mb-2">Import Failed</div>
                <div className="text-sm text-red-500">{progress.error}</div>
              </div>
            ) : (
              <div>
                <div className="mb-4">
                  <div className="text-sm text-text-secondary mb-1">{progress.stage}</div>
                  <div className="text-2xl font-bold">{progress.progress}%</div>
                </div>
                <div className="w-full bg-border rounded-full h-2 mb-4">
                  <div
                    className="bg-accent h-2 rounded-full transition-all"
                    style={{ width: `${progress.progress}%` }}
                  />
                </div>
                {progress.total > 0 && (
                  <div className="text-xs text-text-secondary">
                    {progress.current} / {progress.total}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
